import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import moment from 'moment';
import { ApolloError, useQuery } from '@apollo/client';
import { IQueryDetectionsPaginated } from '../queries';
import { QUERY_DETECTIONS_PAGINATED } from '../queries/useQueryDetectionsPaginated';
import { useGetFirstMostSevereAndLatestDetectionsByEntityName } from '../queries';
import { GetFirstMostSevereAndLatestDetectionsByEntityNameQuery } from '@investigator/shared/generated/graphql';
import { useAgenticTriageSettings } from '@investigator/features/entities/hooks';
import { useEntitiesContext } from '@investigator/features/entities/context';
import {
  useGetEntityTimelineSummary,
  IGetEntityTimelineSummary,
} from '@investigator/features/entities/queries';

const DEFAULT_TIMELINE_INTERVAL = 3600;
const DEFAULT_PAGE_SIZE = 5;
const DEFAULT_PAGE_INDEX = 0;

interface DetectionSummaryContextProps {
  entityDetectionSummary?: GetFirstMostSevereAndLatestDetectionsByEntityNameQuery['getFirstMostSevereAndLatestDetectionsByEntityName'];
  entityDetectionSummaryLoading: boolean;
  entityDetectionsActivityTimelineInterval: number;
  setEntityDetectionsActivityTimelineInterval: React.Dispatch<
    React.SetStateAction<number>
  >;
  entityDetectionsActivityTimelineStart: number;
  entityDetectionsActivityTimelineEnd: number;
  entityDetectionsActivityTimelineSummaryData?: IGetEntityTimelineSummary;
  entityDetectionsActivityTimelineSummaryLoading: boolean;
  entityDetectionsActivityTimelineSummaryError?: ApolloError;
  entityDetectionsActivityData?: IQueryDetectionsPaginated;
  entityDetectionsActivityLoading: boolean;
  entityDetectionsActivityError?: ApolloError;
  entityDetectionsActivityPageIndex: number;
  entityDetectionsActivityPageSize: number;
  entityDetectionsActivityPageCount: number;
  entityDetectionsActivityOffset: number;
  entityDetectionsActivityDateRangeChangeKey: string;
  handleEntityDetectionsActivityPageChange: (
    newPageIndex: number,
    newPageSize: number,
  ) => void;
}

const DetectionSummaryContext = createContext<
  DetectionSummaryContextProps | undefined
>(undefined);

export const useDetectionSummaryContext = () => {
  const context = useContext(DetectionSummaryContext);
  if (!context) {
    throw new Error(
      'useDetectionSummaryContext must be used within a DetectionSummaryProvider',
    );
  }
  return context;
};

export const DetectionSummaryProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [entityDetectionSummary, setEntityDetectionSummary] =
    useState<
      GetFirstMostSevereAndLatestDetectionsByEntityNameQuery['getFirstMostSevereAndLatestDetectionsByEntityName']
    >();
  const [
    entityDetectionsActivityTimelineSummaryData,
    setEntityDetectionsActivityTimelineSummaryData,
  ] = useState<IGetEntityTimelineSummary>();

  const [
    entityDetectionsActivityTimelineSummaryError,
    setEntityDetectionsActivityTimelineSummaryError,
  ] = useState<ApolloError>();

  const [entityDetectionsActivityData, setEntityDetectionsActivityData] =
    useState<IQueryDetectionsPaginated>();

  const [
    entityDetectionsActivityTimelineInterval,
    setEntityDetectionsActivityTimelineInterval,
  ] = useState<number>(DEFAULT_TIMELINE_INTERVAL);

  const [pagination, setPagination] = useState({
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [entityDetectionsActivityError, setEntityDetectionsActivityError] =
    useState<ApolloError>();

  const { selectedEntity } = useEntitiesContext();
  const { agenticTriageEnabled } = useAgenticTriageSettings();

  const entityDetectionsActivityPageCount = useMemo(() => {
    if (!entityDetectionsActivityData?.queryDetectionsPaginated?.total_items)
      return 0;
    return Math.ceil(
      entityDetectionsActivityData.queryDetectionsPaginated.total_items /
        pagination.pageSize,
    );
  }, [entityDetectionsActivityData, pagination.pageSize]);

  // Use normalized dates from selectedEntity (context handles fallback to global dates)
  const selectedEntityStart = selectedEntity?.start ?? 0;
  const selectedEntityEnd = selectedEntity?.end ?? 0;

  const entityDetectionsActivityTimelineEnd = useMemo(
    () => moment().unix(),
    [],
  );
  const entityDetectionsActivityTimelineStart = useMemo(
    () => moment().subtract(7, 'days').unix(),
    [],
  );

  const entityDetectionsActivityDateRangeChangeKey = `${entityDetectionsActivityTimelineStart}-${entityDetectionsActivityTimelineEnd}`;

  const entityDetectionsActivityOffset = useMemo(
    () => pagination.pageIndex * pagination.pageSize,
    [pagination.pageIndex, pagination.pageSize],
  );

  const _entityQuery = useMemo(() => {
    if (!selectedEntity?.entityName) return '';

    return JSON.stringify({
      query: {
        bool: {
          should: [
            {
              term: {
                '_search_keys.source_entities.keyword': {
                  value: selectedEntity.entityName,
                },
              },
            },
            {
              term: {
                '_search_keys.destination_entities.keyword': {
                  value: selectedEntity.entityName,
                },
              },
            },
          ],
          minimum_should_match: 1,
        },
      },
    });
  }, [selectedEntity?.entityName]);

  // TEMPORARILY DISABLED: Entity detections activity UI is hidden due to issues
  const _entityDetectionsActivityFeatureDisabled = true;

  const _skipEntityDetectionsActivityQueries =
    _entityDetectionsActivityFeatureDisabled ||
    !selectedEntity?.entityName ||
    !selectedEntity?.tenantInfo?.tenant_id ||
    !entityDetectionsActivityTimelineEnd ||
    !entityDetectionsActivityTimelineStart ||
    !agenticTriageEnabled;

  const handleEntityDetectionsActivityPageChange = useCallback(
    (newPageIndex: number, newPageSize: number) => {
      setPagination({ pageIndex: newPageIndex, pageSize: newPageSize });
    },
    [],
  );

  const { loading: entityDetectionSummaryLoading } =
    useGetFirstMostSevereAndLatestDetectionsByEntityName({
      skip:
        !selectedEntity?.entityName ||
        !selectedEntityStart ||
        !selectedEntityEnd,
      variables: {
        entity_name: selectedEntity?.entityName ?? '',
        start: selectedEntityStart,
        end: selectedEntityEnd,
        tenant: selectedEntity?.tenantInfo?.tenant_id,
      },
      onCompleted: (
        data: GetFirstMostSevereAndLatestDetectionsByEntityNameQuery,
      ) => {
        // Only update if entity is still selected (avoid stale data)
        if (selectedEntity?.entityName) {
          setEntityDetectionSummary(
            data?.getFirstMostSevereAndLatestDetectionsByEntityName,
          );
        }
      },
      onError: (error: ApolloError) => {
        // Log error for debugging
        console.error('Failed to fetch detection summary:', error);
        // Only clear if entity is still selected (avoid clearing data for new entity)
        if (selectedEntity?.entityName) {
          setEntityDetectionSummary(undefined);
        }
      },
    });

  const { loading: entityDetectionsActivityTimelineSummaryLoading } =
    useGetEntityTimelineSummary({
      skip: _skipEntityDetectionsActivityQueries,
      variables: {
        entity_name: selectedEntity?.entityName,
        detection_tenant: selectedEntity?.tenantInfo?.tenant_id,
        start: entityDetectionsActivityTimelineStart,
        end: entityDetectionsActivityTimelineEnd,
        interval: entityDetectionsActivityTimelineInterval,
        top_n: 5,
        severity_filter: undefined,
        alert_name_filter: undefined,
      },
      onCompleted: (data) => {
        if (selectedEntity?.entityName) {
          setEntityDetectionsActivityTimelineSummaryData(data);
          setEntityDetectionsActivityTimelineSummaryError(undefined);
        }
      },
      onError: (error: ApolloError) => {
        console.error('Failed to fetch entity detections timeline:', error);
        if (selectedEntity?.entityName) {
          setEntityDetectionsActivityTimelineSummaryData(undefined);
        }
        if (error) {
          setEntityDetectionsActivityTimelineSummaryError(error);
        }
      },
    });

  const { loading: entityDetectionsActivityLoading } = useQuery(
    QUERY_DETECTIONS_PAGINATED,
    {
      skip: _skipEntityDetectionsActivityQueries || !selectedEntity?.entityName,
      variables: {
        query: _entityQuery,
        size: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
        sort: [
          {
            sort_by: 'latest_start_timestamp',
            sort_dir: 'desc',
          },
        ],
      },
      onCompleted: (data) => {
        if (data && selectedEntity?.entityName) {
          setEntityDetectionsActivityData(data);
        }
      },
      onError: (error: ApolloError) => {
        console.error('Failed to fetch entity detections:', error);
        if (error) {
          setEntityDetectionsActivityError(error);
        }
      },
    },
  );

  const entityDetectionsActivityPageIndex = pagination.pageIndex;
  const entityDetectionsActivityPageSize = pagination.pageSize;

  const contextValue = useMemo(
    () => ({
      entityDetectionSummary,
      entityDetectionSummaryLoading,
      entityDetectionsActivityTimelineInterval,
      setEntityDetectionsActivityTimelineInterval,
      entityDetectionsActivityTimelineStart,
      entityDetectionsActivityTimelineEnd,
      entityDetectionsActivityTimelineSummaryData,
      entityDetectionsActivityTimelineSummaryLoading,
      entityDetectionsActivityTimelineSummaryError,
      entityDetectionsActivityData,
      entityDetectionsActivityLoading,
      entityDetectionsActivityError,
      entityDetectionsActivityPageIndex,
      entityDetectionsActivityPageSize,
      entityDetectionsActivityPageCount,
      entityDetectionsActivityOffset,
      entityDetectionsActivityDateRangeChangeKey,
      handleEntityDetectionsActivityPageChange,
    }),
    [
      entityDetectionSummary,
      entityDetectionSummaryLoading,
      entityDetectionsActivityTimelineInterval,
      setEntityDetectionsActivityTimelineInterval,
      entityDetectionsActivityTimelineStart,
      entityDetectionsActivityTimelineEnd,
      entityDetectionsActivityTimelineSummaryData,
      entityDetectionsActivityTimelineSummaryLoading,
      entityDetectionsActivityTimelineSummaryError,
      entityDetectionsActivityData,
      entityDetectionsActivityLoading,
      entityDetectionsActivityError,
      entityDetectionsActivityPageIndex,
      entityDetectionsActivityPageSize,
      entityDetectionsActivityPageCount,
      entityDetectionsActivityOffset,
      entityDetectionsActivityDateRangeChangeKey,
      handleEntityDetectionsActivityPageChange,
    ],
  );

  return (
    <DetectionSummaryContext.Provider value={contextValue}>
      {children}
    </DetectionSummaryContext.Provider>
  );
};

export { DetectionSummaryContext };
